import { clamp } from '../shared/track.js';

export interface TiltCalibrationProfile {
  neutral: number;
  deadZone: number;
  negativeSpan: number;
  positiveSpan: number;
  updatedAt: number;
}

interface StoredTiltCalibrations {
  version: 1;
  orientations: Record<string, TiltCalibrationProfile>;
}

const STORAGE_KEY = 'nightshift:tilt-calibration:v1';
const DEFAULT_SPAN = 24;

export function defaultTiltCalibration(neutral = 0, now = Date.now()): TiltCalibrationProfile {
  return {
    neutral: finiteAngle(neutral),
    deadZone: 2,
    negativeSpan: DEFAULT_SPAN,
    positiveSpan: DEFAULT_SPAN,
    updatedAt: now,
  };
}

/** Uses a median/MAD centre so one old-tablet sensor spike cannot bias zero. */
export function calibrateTiltSamples(
  samples: readonly number[],
  previous?: TiltCalibrationProfile,
  now = Date.now(),
): TiltCalibrationProfile {
  const finite = samples.filter(Number.isFinite).map(finiteAngle).slice(-90).sort((a, b) => a - b);
  if (finite.length === 0) return previous ?? defaultTiltCalibration(0, now);
  const neutral = median(finite);
  const deviations = finite
    .map((sample) => Math.abs(signedAngleDifference(sample, neutral)))
    .sort((a, b) => a - b);
  const noise = median(deviations);
  return {
    neutral,
    deadZone: clamp(1.5 + noise * 2.4, 1.5, 5),
    negativeSpan: clamp(previous?.negativeSpan ?? DEFAULT_SPAN, 14, 42),
    positiveSpan: clamp(previous?.positiveSpan ?? DEFAULT_SPAN, 14, 42),
    updatedAt: now,
  };
}

/** Learns comfortable left/right travel without shrinking an established range. */
export function learnTiltRange(
  profile: TiltCalibrationProfile,
  tilt: number,
  now = Date.now(),
): TiltCalibrationProfile {
  const delta = signedAngleDifference(tilt, profile.neutral);
  if (Math.abs(delta) <= profile.deadZone) return profile;
  const learned = clamp(Math.abs(delta) * 1.08, 14, 42);
  if (delta < 0 && learned > profile.negativeSpan + 0.2) {
    return { ...profile, negativeSpan: learned, updatedAt: now };
  }
  if (delta > 0 && learned > profile.positiveSpan + 0.2) {
    return { ...profile, positiveSpan: learned, updatedAt: now };
  }
  return profile;
}

/** Fits a fresh profile only after the driver has demonstrated both sides. */
export function fitTiltRange(
  profile: TiltCalibrationProfile,
  negativeExtent: number,
  positiveExtent: number,
  now = Date.now(),
): TiltCalibrationProfile {
  if (negativeExtent < 6 || positiveExtent < 6) return profile;
  const negativeSpan = clamp(negativeExtent * 1.12, 14, 42);
  const positiveSpan = clamp(positiveExtent * 1.12, 14, 42);
  if (
    Math.abs(negativeSpan - profile.negativeSpan) < 0.2
    && Math.abs(positiveSpan - profile.positiveSpan) < 0.2
  ) return profile;
  return { ...profile, negativeSpan, positiveSpan, updatedAt: now };
}

export function calibratedTiltSteering(
  tilt: number,
  profile: TiltCalibrationProfile,
  sensitivity: number,
  deadZoneOverride = profile.deadZone,
): number {
  const delta = signedAngleDifference(tilt, profile.neutral);
  const deadZone = clamp(deadZoneOverride, 1.2, 6);
  if (Math.abs(delta) <= deadZone) return 0;
  const learnedSpan = delta < 0 ? profile.negativeSpan : profile.positiveSpan;
  const fullLockDegrees = clamp(learnedSpan, 14, 42) / Math.sqrt(clamp(sensitivity, 0.6, 1.8));
  const normalized = clamp((Math.abs(delta) - deadZone) / Math.max(8, fullLockDegrees - deadZone), 0, 1);
  // Keep calibration linear. The device-aware steering controller owns the
  // single user-selectable response curve, avoiding the old double shaping.
  return Math.sign(delta) * normalized;
}

export function loadTiltCalibration(orientationAngle: number): TiltCalibrationProfile | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '') as Partial<StoredTiltCalibrations>;
    if (parsed.version !== 1 || !parsed.orientations || typeof parsed.orientations !== 'object') return null;
    return normaliseProfile(parsed.orientations[orientationKey(orientationAngle)]);
  } catch {
    return null;
  }
}

export function saveTiltCalibration(orientationAngle: number, profile: TiltCalibrationProfile): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) as Partial<StoredTiltCalibrations> : null;
    const orientations = parsed?.version === 1 && parsed.orientations && typeof parsed.orientations === 'object'
      ? { ...parsed.orientations }
      : {};
    orientations[orientationKey(orientationAngle)] = { ...profile };
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, orientations } satisfies StoredTiltCalibrations));
  } catch {
    // Private browsing and locked-down WebViews may reject storage. Runtime
    // calibration remains active for the current race.
  }
}

function normaliseProfile(value: unknown): TiltCalibrationProfile | null {
  if (!value || typeof value !== 'object') return null;
  const source = value as Partial<TiltCalibrationProfile>;
  if (![source.neutral, source.deadZone, source.negativeSpan, source.positiveSpan].every(Number.isFinite)) return null;
  return {
    neutral: finiteAngle(Number(source.neutral)),
    deadZone: clamp(Number(source.deadZone), 1.2, 6),
    negativeSpan: clamp(Number(source.negativeSpan), 14, 42),
    positiveSpan: clamp(Number(source.positiveSpan), 14, 42),
    updatedAt: Number.isFinite(source.updatedAt) ? Number(source.updatedAt) : 0,
  };
}

function orientationKey(angle: number): string {
  const normalized = ((Math.round(angle / 90) * 90) % 360 + 360) % 360;
  return String(normalized);
}

function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const middle = Math.floor(values.length / 2);
  return values.length % 2 === 0
    ? ((values[middle - 1] ?? 0) + (values[middle] ?? 0)) * 0.5
    : values[middle] ?? 0;
}

function finiteAngle(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

function signedAngleDifference(target: number, current: number): number {
  return ((target - current + 540) % 360) - 180;
}
